import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const CreatePostModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "",
    tags: "",
    isQuestion: false,
    allowAnonymous: false
  });

  const categoryOptions = [
    { value: "nutrition", label: "Nutrition & Diet" },
    { value: "fitness", label: "Fitness & Exercise" },
    { value: "mindfulness", label: "Mindfulness & Mental Health" },
    { value: "sleep", label: "Sleep & Recovery" },
    { value: "center-specific", label: "Center Discussions" },
    { value: "challenges", label: "Community Challenges" },
    { value: "general", label: "General Discussion" }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (formData?.title?.trim() && formData?.content?.trim() && formData?.category) {
      onSubmit(formData);
      setFormData({
        title: "",
        content: "",
        category: "",
        tags: "",
        isQuestion: false,
        allowAnonymous: false
      });
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl text-foreground">
            Create New Post
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Post Title"
              type="text"
              placeholder="What's on your mind?"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              required
            />

            <Select
              label="Category"
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => handleInputChange('category', value)}
              placeholder="Select category"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Content
            </label>
            <textarea
              placeholder="Share your thoughts, ask questions, or start a discussion..."
              value={formData?.content}
              onChange={(e) => handleInputChange('content', e?.target?.value)}
              rows={6}
              required
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            />
          </div>

          <Input
            label="Tags (optional)"
            type="text"
            placeholder="Add tags separated by commas (e.g., beginner, tips, motivation)"
            value={formData?.tags}
            onChange={(e) => handleInputChange('tags', e?.target?.value)}
            description="Help others find your post with relevant tags"
          />

          <div className="flex flex-col sm:flex-row gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData?.isQuestion}
                onChange={(e) => handleInputChange('isQuestion', e?.target?.checked)}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring"
              />
              <span className="text-sm text-foreground">Mark as question</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData?.allowAnonymous}
                onChange={(e) => handleInputChange('allowAnonymous', e?.target?.checked)}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring"
              />
              <span className="text-sm text-foreground">Allow anonymous replies</span>
            </label>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Community Guidelines</p>
                <ul className="space-y-1 text-xs">
                  <li>• Be respectful and supportive of all members</li>
                  <li>• Share evidence-based wellness information</li>
                  <li>• Avoid medical advice - encourage professional consultation</li>
                  <li>• Keep discussions relevant to wellness topics</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!formData?.title?.trim() || !formData?.content?.trim() || !formData?.category}
            >
              <Icon name="Send" size={16} />
              <span className="ml-2">Post Discussion</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePostModal;